<template>
  <footer class="edu-footer">
    <b-container>
      <div class="edu-footer__row">
        <!-- Company + contacts -->
        <div class="edu-footer__col edu-footer__col--about">
          <h2 class="edu-footer__heading">Edunet GmbH</h2>
          <p v-if="texts.description" class="edu-footer__desc">{{ texts.description }}</p>
          <ul class="edu-footer__contacts list-unstyled">
            <li>
              <b-icon icon="geo-alt" />
              <span>Luisenstr. 41, 10117 Berlin, Germany</span>
            </li>
            <li>
              <b-icon icon="envelope" />
              <a href="mailto:info@edunet.works">info@edunet.works</a>
            </li>
            <li>
              <b-icon icon="telephone" />
              <a href="tel:+4915563379350">+49 15563 379350</a>
            </li>
            <li>
              <b-icon icon="whatsapp" />
              <a href="https://wa.me/4915563379350" target="_blank" rel="noopener noreferrer">WhatsApp</a>
            </li>
            <li>
              <b-icon icon="linkedin" />
              <a href="https://www.linkedin.com/company/edunet-gmbh" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </li>
          </ul>
        </div>

        <!-- Pages (same list as the header menu) -->
        <div class="edu-footer__col edu-footer__col--pages">
          <h2 class="edu-footer__heading">{{ texts.pages }}</h2>
          <ul class="list-unstyled m-0">
            <li v-for="item in pages" :key="item.key">
              <router-link v-if="item.current" to="/" class="edu-footer__link">{{ item.label }}</router-link>
              <a v-else :href="item.href" class="edu-footer__link" target="_blank" rel="noopener">{{ item.label }}</a>
            </li>
          </ul>
        </div>

        <!-- Services -->
        <div class="edu-footer__col edu-footer__col--services">
          <h2 class="edu-footer__heading">{{ texts.services }}</h2>
          <nav>
            <a :href="page('job-placement')" class="edu-footer__link" target="_blank" rel="noopener">{{ texts.nav.jobPlacement }}</a>
            <a :href="page('learn-german')" class="edu-footer__link" target="_blank" rel="noopener">{{ texts.germanCourses }}</a>
            <a :href="page('educational-network')" class="edu-footer__link" target="_blank" rel="noopener">{{ texts.nav.educationalNetwork }}</a>
            <router-link to="/" class="edu-footer__link">
              {{ texts.nav.talentPool }} <b-icon icon="box-arrow-up-right" class="edu-footer__ext-icon" />
            </router-link>
            <a href="#" class="edu-footer__link edu-footer__link--accent" @click.prevent="openRegistration">
              {{ texts.register }} <b-icon icon="box-arrow-up-right" class="edu-footer__ext-icon" />
            </a>
          </nav>
        </div>

        <!-- Company information -->
        <div class="edu-footer__col edu-footer__col--company">
          <h2 class="edu-footer__heading">
            <b-icon icon="building" class="edu-footer__heading-icon" />
            {{ texts.companyInformation }}
          </h2>
          <ul class="edu-footer__company list-unstyled">
            <li><span class="edu-footer__company-label">{{ texts.managingDirector }}</span><span>Jeniffer Muvai</span></li>
            <li><span class="edu-footer__company-label">{{ texts.companyRegister }}</span><span>HRB 264074 B</span></li>
            <li><span class="edu-footer__company-label">{{ texts.court }}</span><span>Amtsgericht Berlin Charlottenburg</span></li>
            <li><span class="edu-footer__company-label">{{ texts.vatId }}</span><span>DE368496173</span></li>
          </ul>
        </div>
      </div>
    </b-container>

    <div class="edu-footer__bottom">
      <b-container class="edu-footer__bottom-inner">
        <div>© {{ year }} Edunet GmbH. {{ texts.rights }}</div>
        <nav class="edu-footer__legal">
          <a :href="page('content/privacy-policy')" target="_blank" rel="noopener">{{ texts.privacy }}</a>
          <a :href="page('node/19')" target="_blank" rel="noopener">{{ texts.legalNotice }}</a>
        </nav>
      </b-container>
    </div>
  </footer>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule, AuthTab } from '@/store/modules/app';
import { EDUNET_SITE, edunetTexts, toEdunetLanguage } from '@/components/Themes/edunetTexts';

@Component
export default class FooterComponentEdunet extends Vue {
  year = new Date().getFullYear();

  get eduLang() {
    return toEdunetLanguage(AppModule.language);
  }

  get texts() {
    return edunetTexts[this.eduLang];
  }

  get pages() {
    const { nav } = this.texts;

    return [
      { key: 'welcome', label: nav.welcome, href: this.page('welcome') },
      { key: 'educational-network', label: nav.educationalNetwork, href: this.page('educational-network') },
      { key: 'job-placement', label: nav.jobPlacement, href: this.page('job-placement') },
      { key: 'learn-german', label: nav.learnGerman, href: this.page('learn-german') },
      {
        key: 'talent-pool', label: nav.talentPool, href: '/', current: true,
      },
      { key: 'candidates', label: nav.talents, href: this.page('candidates') },
      { key: 'about-us', label: nav.aboutUs, href: this.page('about-us') },
      { key: 'contact', label: nav.contact, href: this.page('contact') },
    ];
  }

  page(path: string) {
    return `${EDUNET_SITE}/${this.eduLang}/${path}`;
  }

  // "Register, it's free" points to this portal on edunet.works — here it opens our applicant registration
  openRegistration() {
    AppModule.SET_SELECTED_AUTH_TAB(AuthTab.applicant);
    this.$bvModal.show('register-modal');
  }
}
</script>

<style lang="scss" scoped>
// Copy of the edunet.works footer (.edu-footer); width follows our container.
.edu-footer {
  --edu-muted: #888;
  --edu-link: #aaa;
  --edu-dim: #555;

  padding: 52px 0 0;
  background-color: #1a1a1a;
  font-size: 1rem;

  @media (max-width: 767.98px) {
    padding-top: 40px;
  }

  a {
    text-decoration: none;
    transition: color 0.2s ease;
    cursor: pointer;
  }
}

.edu-footer__row {
  display: flex;
  flex-wrap: wrap;
  margin: -1.5rem -0.75rem 0;

  @media (min-width: 1200px) {
    margin: -3rem -1.5rem 0;
  }
}

.edu-footer__col {
  padding: 1.5rem 0.75rem 0;
  width: 100%;

  @media (min-width: 1200px) {
    padding: 3rem 1.5rem 0;
  }
}

.edu-footer__col--pages,
.edu-footer__col--services {
  width: 50%;
}

@media (min-width: 576px) {
  .edu-footer__col--about,
  .edu-footer__col--company {
    width: 50%;
  }

  // services get more room so "Kostenlos registrieren ↗" stays on one line
  .edu-footer__col--pages { width: 20%; }
  .edu-footer__col--services { width: 30%; }
}

@media (min-width: 992px) {
  .edu-footer__col--about { width: 33.3333%; }
  .edu-footer__col--pages { width: 16.6667%; }
  .edu-footer__col--services,
  .edu-footer__col--company { width: 25%; }
}

// doubled class beats the global `[theme] body.portal h2` colour/size rules
.edu-footer .edu-footer__heading {
  display: flex;
  align-items: center;
  margin: 0 0 1rem;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.edu-footer__heading-icon {
  margin-right: 0.5rem;
  color: var(--portal-primary);
  font-size: 0.9rem;
}

.edu-footer__desc {
  margin-bottom: 1.125rem;
  color: var(--edu-muted);
  font-size: 0.8625rem;
  line-height: 1.75;
}

.edu-footer__contacts {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    color: var(--edu-muted);
    font-size: 0.8625rem;
    line-height: 1.45;
  }

  .b-icon {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--portal-primary);
    font-size: 0.9rem;
  }

  a {
    color: var(--edu-link);

    &:hover {
      color: var(--portal-primary);
    }
  }
}

.edu-footer__link {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--edu-link);
  font-size: 0.8625rem;
  line-height: 1.4;

  &:hover {
    color: var(--portal-primary);
  }
}

.edu-footer__link--accent {
  color: var(--portal-primary);
  font-weight: 600;

  &:hover {
    color: color-mix(in srgb, var(--portal-primary) 88%, #000);
  }
}

.edu-footer__ext-icon {
  margin-left: 3px;
  font-size: 0.65rem;
  vertical-align: middle;
  opacity: 0.55;
}

.edu-footer__company {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin: 0;

  li {
    display: flex;
    flex-direction: column;
    gap: 1px;
    color: var(--edu-link);
    font-size: 0.8625rem;
    line-height: 1.45;
  }
}

.edu-footer__company-label {
  color: var(--edu-dim);
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.edu-footer__bottom {
  margin-top: 44px;
  padding: 1rem 0;
  border-top: 1px solid #252525;
  color: var(--edu-dim);
  font-size: 0.8125rem;
}

.edu-footer__bottom-inner {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.edu-footer__legal {
  display: flex;
  flex-wrap: wrap;

  a {
    margin-left: 1.25rem;
    color: var(--edu-dim);

    &:hover {
      color: var(--portal-primary);
    }

    @media (max-width: 767.98px) {
      margin: 0 1rem 0 0;
    }
  }
}
</style>
